import { useRef, useState } from "react";
import axios from "axios";
import { UploadCloud, FileCheck2, X, Loader2, Car, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { fileToBase64 } from "../wizard/wizardUtils";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Render the first few pages of a PDF (data URL) to JPEG data URLs so the
// server can extract via free image vision (no OpenRouter PDF file fee).
async function pdfToImages(dataUrl, maxPages = 4, scale = 2) {
  const b64 = dataUrl.split(",")[1];
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
  const pages = Math.min(pdf.numPages, maxPages);
  const images = [];
  for (let p = 1; p <= pages; p++) {
    const page = await pdf.getPage(p);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
    images.push(canvas.toDataURL("image/jpeg", 0.85));
  }
  return images;
}

const FIELD_LABELS = {
  documentType: "Document",
  insurer: "Insurer",
  policyNumber: "Policy / Quote No.",
  insuredName: "Insured Name",
  vehicleRegNo: "Vehicle Reg No.",
  makeModel: "Make / Model",
  year: "Year",
  fuel: "Fuel",
  idv: "IDV",
  ncbPercent: "NCB %",
  odPremium: "OD Premium",
  tpPremium: "TP Premium",
  totalPremium: "Total Premium",
  policyStart: "Policy Start",
  policyEnd: "Policy End",
};

export default function MotorUpload() {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null); // { name, type, base64 }
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const pick = async (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10MB");
      return;
    }
    const base64 = await fileToBase64(f);
    setFile({ name: f.name, type: f.type, base64 });
  };

  const submit = async () => {
    if (!file) {
      toast.error("Please upload your motor policy or quote.");
      return;
    }
    setSubmitting(true);
    try {
      let images = null;
      if (file.type === "application/pdf") {
        images = await pdfToImages(file.base64);
        if (!images.length) throw new Error("Could not read the PDF.");
      }
      const res = await axios.post(`${API}/motor`, {
        fileName: file.name,
        fileType: file.type,
        fileBase64: file.base64,
        images,
      });
      setResult(res.data);
      toast.success("Details captured");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setFile(null);
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center px-5 sm:px-8 py-16 sm:py-20">
      <div className="max-w-xl w-full">
        <div className="inline-flex items-center gap-2 mb-6">
          <span className="w-10 h-10 rounded-full accent-bg text-white flex items-center justify-center">
            <Car size={20} />
          </span>
          <span className="text-lg font-medium tracking-tight">Motor Insurance</span>
        </div>

        {!result ? (
          <>
            <h2 className="text-3xl sm:text-4xl font-medium tracking-tight mb-2">
              Upload your policy or quote
            </h2>
            <p className="text-secondary-brand mb-8 leading-relaxed">
              Share your current motor policy or the quote you received — we&apos;ll
              read the details for you automatically.
            </p>

            <div className="bg-surface border border-subtle rounded-2xl p-6 mb-6">
              {file ? (
                <div
                  className="flex items-center justify-between p-4 rounded-xl bg-base border border-subtle"
                  data-testid="motor-file"
                >
                  <div className="flex items-center gap-3">
                    <FileCheck2 className="text-[color:var(--accent)]" size={22} />
                    <div>
                      <p className="text-sm font-medium">{file.name}</p>
                      <p className="text-xs text-muted-brand">Ready to process</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="text-muted-brand hover:text-primary-brand"
                    aria-label="Remove file"
                  >
                    <X size={20} />
                  </button>
                </div>
              ) : (
                <div
                  className={`dropzone ${dragging ? "dragging" : ""}`}
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    pick(e.dataTransfer?.files?.[0]);
                  }}
                  data-testid="motor-dropzone"
                  role="button"
                  tabIndex={0}
                >
                  <UploadCloud className="text-secondary-brand mb-3" size={34} />
                  <p className="text-base font-semibold mb-1">
                    Drop your file here, or click to browse
                  </p>
                  <p className="text-xs text-muted-brand">
                    PDF, JPG, PNG · Max 10MB · Policy schedule or quotation
                  </p>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".pdf,image/*"
                    hidden
                    onChange={(e) => pick(e.target.files?.[0])}
                    data-testid="motor-input"
                  />
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={submitting || !file}
              className="btn-primary w-full justify-center disabled:opacity-50"
              data-testid="motor-submit"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Reading your document…
                </>
              ) : (
                "Submit"
              )}
            </button>
          </>
        ) : (
          <div data-testid="motor-result">
            <div className="flex items-center gap-2 mb-2 text-[color:var(--accent)]">
              <CheckCircle2 size={24} />
              <h2 className="text-2xl font-medium tracking-tight text-primary-brand">
                Details captured
              </h2>
            </div>
            <p className="text-secondary-brand mb-6">
              Reference <span className="font-medium">{result.referenceNumber}</span>
            </p>

            <div className="bg-surface border border-subtle rounded-2xl p-6 mb-6">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {Object.keys(FIELD_LABELS).map((k) => {
                  const v = result.extracted?.[k];
                  if (!v) return null;
                  return (
                    <div key={k} className="flex flex-col">
                      <dt className="text-xs uppercase tracking-widest text-muted-brand">
                        {FIELD_LABELS[k]}
                      </dt>
                      <dd className="text-sm font-medium">{v}</dd>
                    </div>
                  );
                })}
              </dl>
              {result.driveLink && (
                <a
                  href={result.driveLink}
                  target="_blank"
                  rel="noreferrer"
                  className="footer-link inline-block mt-5 text-sm"
                >
                  View uploaded document →
                </a>
              )}
            </div>

            <button type="button" onClick={reset} className="btn-secondary" data-testid="motor-again">
              Upload another
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
